import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
} from "@auth0/auth0-react";
import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resolvePostAuthTarget } from "./lib/postAuthTarget";

// The Auth0 `redirect_uri`. It is the only callback whose `code`/`state`
// belong to the Auth0 SDK; /auth/wealthbox and /auth/schwab carry a service's
// own `code`/`state`, which the SDK would otherwise try to redeem as an Auth0
// login, fail with "Invalid state", and send the popup to sign-in (APP-2183).
export const AUTH0_CALLBACK_PATH = "/auth/auth0";

const Auth0ProviderWithRedirectCallback = ({
  children,
  ...props
}: Auth0ProviderOptions) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      // The post-auth destination must be a real app route, never the OAuth
      // callback: its single-use code is already consumed by this point, so
      // navigating back to /auth/* replays a dead code and re-enters the
      // auth guard → redirect loop. resolvePostAuthTarget defaults to "/" and
      // rejects any /auth/* target. (redirect_uri still routes the OAuth
      // round-trip through the callback — this only controls where the app
      // lands afterwards.)
      // APP-2048: replace the callback entry, so Back shows the previous page
      // and not "Authorizing..." on /auth/auth0. APP-2042: likewise, "Cancel"
      // on the first page after sign-in must not go back to the callback.
      navigate(resolvePostAuthTarget(appState?.returnTo), { replace: true });
    },
    [navigate],
  );

  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      skipRedirectCallback={pathname !== AUTH0_CALLBACK_PATH}
      {...props}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithRedirectCallback;
